// Render helpers for iView Table cells, styled by request-table.scss
import moment from 'moment';

const LEAVE_TYPES = {
    vl: 'Vacation Leave',
    sl: 'Sick Leave',
    al: 'Appreciation Leave',
};

export const formatLeaveType = (type) => {
    if (!type) return '—';
    return LEAVE_TYPES[type.toLowerCase()] || type.toUpperCase();
};

// Leave credit types come as `LEAVE|SL`, `LEAVE|VL`, ...
export const formatCreditType = (type) => {
    if (!type) return '—';
    return formatLeaveType(type.split('|').pop());
};

export const formatStatus = (status) => {
    if (!status) return '—';
    const label = status.replace(/_/g, ' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
};

// Days an admin cancelled out of an approved leave: [{ date: 'YYYY-MM-DD', reason, cancelledAt }]
export const getCancelledLeaveDays = (row) => (row && row.requestedData && row.requestedData.cancelledDays) || [];

// An approved leave with some days cancelled stays `approved` in the API.
// A single-day row (see expandLeaveDays) already carries its own status.
export const getLeaveStatus = (row) => {
    if (!row || row.leaveDate) return row && row.status;
    return row.status === 'approved' && getCancelledLeaveDays(row).length ? 'partially_cancelled' : row.status;
};

// Working days (YYYY-MM-DD) covered by a leave, same rule as the API
export const getLeaveWorkingDates = (row) => {
    if (!row || !row.requestedStart || !row.requestedEnd) return [];
    const dates = [];
    const day = moment.utc(row.requestedStart).startOf('day');
    const end = moment.utc(row.requestedEnd).startOf('day');
    while (day.isSameOrBefore(end)) {
        if (![0, 6].includes(day.day())) dates.push(day.format('YYYY-MM-DD'));
        day.add(1, 'day');
    }
    return dates;
};

// Lists an approved leave as one row per day so the admin can cancel a single day.
// Pending/rejected leave stays one row because it is approved or rejected as a whole.
export const expandLeaveDays = (rows) =>
    rows.flatMap((row) => {
        const cancelledDates = getCancelledLeaveDays(row).map((day) => day.date);
        const isExpandable = row.status === 'approved' || (row.status === 'cancelled' && cancelledDates.length > 0);
        const dates = isExpandable ? getLeaveWorkingDates(row) : [];
        if (!dates.length) return [row];

        return dates.map((date) => ({
            ...row,
            rowKey: `${row._id}-${date}`,
            leaveDate: date,
            requestedStart: `${date}T00:00:00.000Z`,
            requestedEnd: `${date}T00:00:00.000Z`,
            status: row.status === 'cancelled' || cancelledDates.includes(date) ? 'cancelled' : row.status,
        }));
    });

export const renderStatus = (h, status) =>
    h('span', { class: ['request-status', `request-status--${status}`] }, [
        h('span', { class: 'request-status__dot' }),
        formatStatus(status),
    ]);

// buttons: [{ content, icon, onClick, danger?, success? }]
export const renderActions = (h, buttons) =>
    h(
        'div',
        { class: 'request-actions' },
        buttons.map(({ content, icon, onClick, danger, success }) =>
            h('Tooltip', { props: { placement: 'top', content, transfer: true } }, [
                h('Button', {
                    class: [
                        'request-actions__icon',
                        { 'request-actions__cancel': danger, 'request-actions__approve': success },
                    ],
                    props: { icon, shape: 'circle' },
                    on: { click: onClick },
                }),
            ])
        )
    );
